import { z } from 'zod';
/**
 * Shared primitive schemas. Every DTO in the system is composed from these so
 * that the API, the database layer and the React forms agree on one definition.
 */
/** ULID: lexicographically sortable, 26 chars, Crockford base32. */
export const ulidSchema = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'must be a valid ULID');
export const emailSchema = z.string().trim().toLowerCase().pipe(z.email().max(254));
/**
 * Password policy, enforced identically on the client form and the server.
 * Length first: it is the only property that reliably resists offline cracking.
 */
export const passwordSchema = z
    .string()
    .min(12, 'password must be at least 12 characters')
    .max(128)
    .refine((v) => /[a-z]/.test(v), 'password must contain a lowercase letter')
    .refine((v) => /[A-Z]/.test(v), 'password must contain an uppercase letter')
    .refine((v) => /\d/.test(v), 'password must contain a digit');
export const isoDateTimeSchema = z.iso.datetime({ offset: true });
/** Locales the product ships. Arabic is the default and comes first. */
export const LOCALES = ['ar', 'en'];
export const localeSchema = z.enum(LOCALES);
/** Arabic is the product's default language. */
export const DEFAULT_LOCALE = 'ar';
/** Text directionality per locale, so the UI never hardcodes a guess. */
export const LOCALE_DIRECTION = {
    ar: 'rtl',
    en: 'ltr',
};
/**
 * Money and rates cross the wire as decimal STRINGS, never as JS numbers.
 * A double cannot hold 0.1 exactly; a currency system that uses one will
 * eventually lose money. Parse with Decimal.js (API) or Intl (display).
 *
 * MONEY_SCALE / RATE_SCALE mirror the Prisma Decimal(15,5) columns exactly
 * (currency.md section 2: five decimal places, half-up).
 */
export const MONEY_PRECISION = 15;
export const MONEY_SCALE = 5;
export const RATE_PRECISION = 15;
export const RATE_SCALE = 5;
const decimalString = (scale, label) => z
    .string()
    .trim()
    .regex(new RegExp(`^-?\\d{1,${MONEY_PRECISION - scale}}(\\.\\d{1,${scale}})?$`), `${label} must be a decimal string with at most ${scale} fraction digits`);
export const moneySchema = decimalString(MONEY_SCALE, 'amount');
export const rateSchema = decimalString(RATE_SCALE, 'rate');
/** ISO 4217 alphabetic currency code. */
export const currencyCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3}$/, 'must be a 3-letter ISO 4217 code');
//# sourceMappingURL=primitives.js.map